import React, { useEffect, useRef, useState } from 'react';
import {
  ActionIcon, Badge, Box, Button, Checkbox, Divider, Group, Loader,
  MultiSelect, NumberInput, Paper, Select, Stack, Text,
  Textarea, TextInput, Title, Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconHelp, IconLock, IconLockOpen, IconPlus, IconTrash } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import {
  AttendanceStatus,
  ParticipantGrade,
  ParticipantResponse,
  TrainingEvent,
  TrainingEventStatus,
  TrainingEventType,
  TrainingParticipant,
} from '../types/training-event.type';
import { Tag } from '../types/tag.type';
import { Person } from '../types/person.type';
import { useUserContext } from '../providers/UserContext';
import { USER_RIGHTS } from '../constants/userRights';
import { startTrainingDetailTour, startTrainingDetailTourOnFirstVisit } from '../helpers/trainingTour';

function minsToTime(mins: number): string {
  return `${Math.floor(mins / 60).toString().padStart(2, '0')}:${(mins % 60).toString().padStart(2, '0')}`;
}

function timeToMins(time: string): number {
  const [h, m] = (time || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

const EVENT_TYPE_OPTIONS = [
  { label: 'Training', value: TrainingEventType.TRAINING },
  { label: 'Spiel', value: TrainingEventType.MATCH },
  { label: 'Veranstaltung', value: TrainingEventType.EVENT },
];

const eventClient = new HttpClient<TrainingEvent>('/training-event');

const TrainingEventDetail: React.FC<{ id?: number }> = ({ id }) => {
  const navigate = useNavigate();
  const { userRights } = useUserContext();
  const isNew = !id;
  const canAdminEdit = userRights.includes(USER_RIGHTS.TRAINING);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grading, setGrading] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);

  const [event, setEvent] = useState<TrainingEvent | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [participants, setParticipants] = useState<TrainingParticipant[]>([]);

  const [tagIds, setTagIds] = useState<string[]>([]);
  const [eventType, setEventType] = useState<string>(TrainingEventType.TRAINING);
  const [assignedPersonId, setAssignedPersonId] = useState<string | null>(null);
  const [coTrainerIds, setCoTrainerIds] = useState<string[]>([]);
  const [date, setDate] = useState<string | null>(null);
  const [startTimeStr, setStartTimeStr] = useState('09:00');
  const [duration, setDuration] = useState<number | string>(60);
  const [notes, setNotes] = useState('');
  const [addPersonId, setAddPersonId] = useState<string | null>(null);
  const [meId, setMeId] = useState<number | null>(null);

  // cancel flow
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // batch-add from tag-matched persons
  const [batchSelected, setBatchSelected] = useState<string[]>([]);

  const gradeNoteRefs = useRef<Record<number, string>>({});

  const isGraded = event?.status === TrainingEventStatus.GRADED;
  const isLocked = isGraded && !adminUnlocked;
  const isExisting = !!id && !!event;
  const canEditGrades = isGraded && (adminUnlocked || meId === Number(assignedPersonId));

  useEffect(() => {
    const tagClient = new HttpClient<Tag>('/tag');
    const personClient = new HttpClient<Person>('/person');
    const meClient = new HttpClient<Person>('/person/me');

    Promise.all([
      tagClient.getAll(),
      personClient.getAll({ slim: 'true' }),
      meClient.getSingle().catch(() => null),
      id ? eventClient.getById(id) : Promise.resolve(null),
    ]).then(([allTags, allPersons, me, eventRes]) => {
      setTags(allTags as Tag[]);
      setPersons(allPersons as Person[]);

      if (me?.id) setMeId(me.id);
      if (eventRes) {
        const ev = (eventRes as any).data as TrainingEvent;
        setEvent(ev);
        setTagIds((ev.tags ?? []).map((t) => t.id.toString()));
        setEventType(ev.eventType ?? TrainingEventType.TRAINING);
        setAssignedPersonId(ev.assignedPerson?.id?.toString() ?? null);
        setCoTrainerIds((ev.coTrainers ?? []).map((p) => p.id.toString()));
        setDate(ev.date ?? null);
        setStartTimeStr(minsToTime(ev.startTime ?? 0));
        setDuration(ev.duration ?? 60);
        setNotes(ev.notes ?? '');
        setParticipants(ev.participants ?? []);
      } else if (me?.id) {
        setAssignedPersonId(me.id.toString());
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!tagIds.length || !assignedPersonId || !date) {
      notifications.show({ color: 'red', message: 'Bitte mindestens einen Tag, eine Person und ein Datum ausfüllen.' });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        id: id ?? 0,
        tags: tagIds.map((tid) => ({ id: Number(tid) })),
        eventType,
        assignedPerson: { id: Number(assignedPersonId) },
        coTrainers: coTrainerIds.map((pid) => ({ id: Number(pid) })),
        date,
        startTime: timeToMins(startTimeStr),
        duration: Number(duration),
        notes: notes || undefined,
        status: event?.status ?? TrainingEventStatus.OPEN,
      };
      const res: any = await eventClient.post(payload);
      notifications.show({ color: 'green', message: 'Gespeichert.' });
      if (isNew) {
        navigate(`/training/events/edit/${res?.data?.id ?? ''}`);
      } else {
        const refreshed = await eventClient.getById(id!);
        const ev = (refreshed as any).data as TrainingEvent;
        setEvent(ev);
        setTagIds((ev.tags ?? []).map((t) => t.id.toString()));
        setCoTrainerIds((ev.coTrainers ?? []).map((p) => p.id.toString()));
        setParticipants(ev.participants ?? []);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Speichern.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Trainingseinheit löschen?')) return;
    try {
      const res = await eventClient.delete(id);
      if (res.status !== 200) throw new Error();
      navigate('/training/events');
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Löschen.' });
    }
  };

  const handleGrade = async () => {
    if (!id || !confirm('Einheit abschließen und bewerten?')) return;
    setGrading(true);
    try {
      const gradeClient = new HttpClient<TrainingEvent>(`/training-event/${id}`);
      const res: any = await gradeClient.postAction('grade');
      const ev = res?.data as TrainingEvent;
      setEvent(ev);
      setParticipants(ev?.participants ?? []);
      notifications.show({ color: 'green', message: 'Einheit wurde bewertet.' });
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Bewerten.' });
    } finally {
      setGrading(false);
    }
  };

  const handleRemindPending = async () => {
    if (!id) return;
    setReminding(true);
    try {
      const remindClient = new HttpClient<TrainingEvent>(`/training-event/${id}`);
      const res: any = await remindClient.postAction('remind');
      notifications.show({
        color: 'green',
        message: `Erinnerung an ${res?.sent ?? 0} Person(en) gesendet${res?.skipped ? `, ${res.skipped} ohne E-Mail-Adresse übersprungen` : ''}.`,
      });
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Senden der Erinnerung.' });
    } finally {
      setReminding(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    try {
      const cancelClient = new HttpClient<TrainingEvent>(`/training-event/${id}`);
      await cancelClient.postAction('cancel', cancelReason ? { reason: cancelReason } : {});
      const refreshed = await eventClient.getById(id);
      const ev = (refreshed as any).data as TrainingEvent;
      setEvent(ev);
      setShowCancelForm(false);
      notifications.show({ color: 'orange', message: 'Einheit wurde storniert.' });
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Stornieren.' });
    }
  };

  const handleAddParticipant = async () => {
    if (!addPersonId || !id) return;
    setAddingParticipant(true);
    try {
      const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
      const res: any = await participantClient.post({ personId: Number(addPersonId), attendanceStatus: AttendanceStatus.PRESENT } as any);
      const newP = res?.data as TrainingParticipant;
      setParticipants((prev) => [...prev, newP]);
      setAddPersonId(null);
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Hinzufügen.' });
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleBatchAdd = async () => {
    if (!id || !batchSelected.length) return;
    const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
    const results: TrainingParticipant[] = [];
    for (const pid of batchSelected) {
      try {
        const res: any = await participantClient.post({ personId: Number(pid), attendanceStatus: AttendanceStatus.PRESENT } as any);
        if (res?.data) results.push(res.data as TrainingParticipant);
      } catch {
        // skip already-added persons
      }
    }
    setParticipants((prev) => [...prev, ...results]);
    setBatchSelected([]);
    notifications.show({ color: 'green', message: `${results.length} Teilnehmer hinzugefügt.` });
  };

  const handleRemoveParticipant = async (participantId: number) => {
    if (!id) return;
    try {
      const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
      await participantClient.delete(participantId);
      setParticipants((prev) => prev.filter((p) => p.id !== participantId));
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Entfernen.' });
    }
  };

  const handleAttendanceChange = async (participantId: number, value: string) => {
    if (!id) return;
    setParticipants((prev) =>
      prev.map((p) => (p.id === participantId ? { ...p, attendanceStatus: value as AttendanceStatus } : p)),
    );
    try {
      const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
      await participantClient.patch(participantId, { attendanceStatus: value } as any);
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Aktualisieren.' });
    }
  };

  const handleGradeChange = async (participantId: number, grade: ParticipantGrade) => {
    if (!id) return;
    setParticipants((prev) => prev.map((p) => (p.id === participantId ? { ...p, grade } : p)));
    try {
      const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
      const gradeNote = gradeNoteRefs.current[participantId] ?? participants.find((p) => p.id === participantId)?.gradeNote ?? '';
      await participantClient.patch(participantId, { grade, gradeNote } as any);
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Speichern der Note.' });
    }
  };

  const handleGradeNoteSave = async (participantId: number, gradeNote: string) => {
    if (!id) return;
    const participant = participants.find((p) => p.id === participantId);
    if (!participant?.grade) return;
    try {
      const participantClient = new HttpClient<TrainingParticipant>(`/training-event/${id}/participant`);
      await participantClient.patch(participantId, { grade: participant.grade, gradeNote } as any);
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Speichern der Notiz.' });
    }
  };

  // Start the tutorial once the form has rendered (anchors don't exist while loading).
  useEffect(() => {
    if (!loading) startTrainingDetailTourOnFirstVisit(isNew);
  }, [loading, isNew]);

  if (loading) return <Loader mt="xl" />;

  const participantPersonIds = new Set(participants.map((p) => p.person?.id));

  // Persons tagged "Inaktiv" are excluded from every add-participant list below,
  // regardless of whether they otherwise match the event's tags.
  const inactiveTagIds = new Set(
    tags.filter((t) => t.name.trim().toLowerCase() === 'inaktiv').map((t) => t.id),
  );
  const isInactive = (p: Person) =>
    Array.isArray(p.tags) && p.tags.some((t) => inactiveTagIds.has(t.id));

  // Persons who share at least one tag with the event — for batch-add
  const selectedTagIdNums = tagIds.map(Number);
  const tagMatchedPersons = persons.filter(
    (p) =>
      !participantPersonIds.has(p.id) &&
      !isInactive(p) &&
      Array.isArray(p.tags) &&
      p.tags.some((t) => selectedTagIdNums.includes(t.id)),
  );

  const addPersonOptions = persons
    .filter((p) => !participantPersonIds.has(p.id) && !isInactive(p))
    .map((p) => ({ value: p.id.toString(), label: `${p.firstName} ${p.lastName}` }));

  const tagOptions = tags.map((t) => ({ value: t.id.toString(), label: t.name }));
  const personOptions = persons.map((p) => ({ value: p.id.toString(), label: `${p.firstName} ${p.lastName}` }));
  const hasPresentParticipant = participants.some((p) => p.attendanceStatus === AttendanceStatus.PRESENT);
  const pendingCount = participants.filter((p) => p.response === ParticipantResponse.PENDING).length;

  const responseBadge = (response: ParticipantResponse) => {
    switch (response) {
      case ParticipantResponse.CONFIRMED:
        return <Badge size="sm" color="green" variant="light">Zugesagt</Badge>;
      case ParticipantResponse.DECLINED:
        return <Badge size="sm" color="red" variant="light">Abgesagt</Badge>;
      default:
        return <Badge size="sm" color="gray" variant="light">Ausstehend</Badge>;
    }
  };

  return (
    <Stack p="md" gap="md" pb={120}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Title order={2}>{isNew ? 'Neue Trainingseinheit' : 'Trainingseinheit'}</Title>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon variant="subtle" size="lg" onClick={() => startTrainingDetailTour(isNew)} title="Anleitung">
            <IconHelp size={20} />
          </ActionIcon>
          {event?.isCancelled && (
            <Badge color="red" variant="filled" size="lg">Storniert</Badge>
          )}
          {event && (
            <Badge
              color={isGraded ? 'gray' : 'green'}
              variant="filled"
              size="lg"
              leftSection={isGraded ? <IconLock size={12} /> : undefined}
            >
              {isGraded ? 'Bewertet' : 'Offen'}
            </Badge>
          )}
          {isGraded && canAdminEdit && (
            <ActionIcon
              variant={adminUnlocked ? 'filled' : 'light'}
              color="orange"
              size="lg"
              onClick={() => setAdminUnlocked((v) => !v)}
              title="Admin: Bearbeitung freigeben"
            >
              {adminUnlocked ? <IconLockOpen size={18} /> : <IconLock size={18} />}
            </ActionIcon>
          )}
        </Group>
      </Group>

      {/* Section 1: Event form */}
      <Paper shadow="sm" p="md" radius="md" withBorder data-tour="event-form">
        <Stack gap="sm">
          <Select
            label="Typ"
            data={EVENT_TYPE_OPTIONS}
            value={eventType}
            onChange={(v) => setEventType(v ?? TrainingEventType.TRAINING)}
            disabled={isLocked}
          />
          <MultiSelect
            label="Tags"
            data={tagOptions}
            value={tagIds}
            onChange={setTagIds}
            searchable
            disabled={isLocked}
            required
          />
          <Select
            label="Trainer / Verantwortlich"
            data={personOptions}
            value={assignedPersonId}
            onChange={(v) => {
              setAssignedPersonId(v);
              setCoTrainerIds((prev) => prev.filter((pid) => pid !== v));
            }}
            searchable
            disabled={isLocked}
            required
          />
          <MultiSelect
            label="Weitere Trainer"
            data={personOptions.filter((p) => p.value !== assignedPersonId)}
            value={coTrainerIds}
            onChange={setCoTrainerIds}
            searchable
            disabled={isLocked}
          />
          <DateInput
            label="Datum"
            value={date}
            onChange={setDate}
            disabled={isLocked}
            required
            valueFormat="DD.MM.YYYY"
          />
          <TimeInput
            label="Startzeit"
            value={startTimeStr}
            onChange={(e) => setStartTimeStr(e.currentTarget.value)}
            disabled={isLocked}
          />
          <NumberInput
            label="Dauer (Minuten)"
            value={duration}
            onChange={setDuration}
            min={1}
            step={15}
            disabled={isLocked}
            required
          />
          <Textarea
            label="Notizen"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            disabled={isLocked}
            autosize
            minRows={2}
          />
        </Stack>
      </Paper>

      {/* Section 2: Participants */}
      {isExisting && (
        <Paper shadow="sm" p="md" radius="md" withBorder data-tour="participants">
          <Group justify="space-between" align="center" mb="sm">
            <Title order={4}>
              Teilnehmer ({participants.length})
            </Title>
            {!isLocked && !event?.isCancelled && pendingCount > 0 && (
              <Button
                size="xs"
                variant="light"
                onClick={handleRemindPending}
                loading={reminding}
              >
                Erinnerung an {pendingCount} Ausstehende senden
              </Button>
            )}
          </Group>
          <Stack gap="xs">
            {participants.length === 0 && (
              <Text c="dimmed" size="sm">Keine Teilnehmer</Text>
            )}

            {participants.map((p) => {
              const canChangeAttendance = !isLocked;
              return (
                <React.Fragment key={p.id}>
                  <Group gap="xs" wrap="wrap" align="center">
                    <Text fw={500} size="sm" style={{ minWidth: 100, flex: 1 }}>
                      {p.person?.firstName} {p.person?.lastName}
                    </Text>

                    {p.respondedByName && p.response !== ParticipantResponse.PENDING ? (
                      <Tooltip
                        label={`von ${p.respondedByName}${p.respondedAt ? `, ${new Date(p.respondedAt).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}`}
                      >
                        <span>{responseBadge(p.response)}</span>
                      </Tooltip>
                    ) : (
                      responseBadge(p.response)
                    )}

                    <Button.Group>
                      <Button
                        size="md"
                        variant={p.attendanceStatus === AttendanceStatus.PRESENT ? 'filled' : 'light'}
                        color="green"
                        onClick={() => handleAttendanceChange(p.id, AttendanceStatus.PRESENT)}
                        disabled={!canChangeAttendance}
                        px="sm"
                      >✓</Button>
                      <Button
                        size="md"
                        variant={p.attendanceStatus === AttendanceStatus.EXCUSED ? 'filled' : 'light'}
                        color="yellow"
                        onClick={() => handleAttendanceChange(p.id, AttendanceStatus.EXCUSED)}
                        disabled={!canChangeAttendance}
                        px="sm"
                      >~</Button>
                      <Button
                        size="md"
                        variant={p.attendanceStatus === AttendanceStatus.UNEXCUSED ? 'filled' : 'light'}
                        color="red"
                        onClick={() => handleAttendanceChange(p.id, AttendanceStatus.UNEXCUSED)}
                        disabled={!canChangeAttendance}
                        px="sm"
                      >✗</Button>
                    </Button.Group>

                    {isGraded && p.attendanceStatus === AttendanceStatus.PRESENT && (
                      <Button.Group>
                        {([ParticipantGrade.A, ParticipantGrade.B, ParticipantGrade.C]).map((g) => (
                          <Button
                            key={g}
                            size="md"
                            variant={p.grade === g ? 'filled' : 'light'}
                            color="blue"
                            onClick={() => handleGradeChange(p.id, g)}
                            disabled={!canEditGrades}
                            px="sm"
                          >{g}</Button>
                        ))}
                      </Button.Group>
                    )}

                    {isGraded && p.attendanceStatus === AttendanceStatus.PRESENT && (
                      <TextInput
                        size="sm"
                        placeholder="Notiz"
                        defaultValue={p.gradeNote ?? ''}
                        disabled={!canEditGrades}
                        onChange={(e) => { gradeNoteRefs.current[p.id] = e.currentTarget.value; }}
                        onBlur={(e) => handleGradeNoteSave(p.id, e.currentTarget.value)}
                        style={{ flex: 1, minWidth: 100 }}
                      />
                    )}

                    {!isLocked && (
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="lg"
                        onClick={() => handleRemoveParticipant(p.id)}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    )}
                  </Group>
                  <Divider my={4} />
                </React.Fragment>
              );
            })}

            {!isLocked && (
              <Group gap="xs" mt="xs" wrap="nowrap">
                <Select
                  placeholder="Person hinzufügen..."
                  data={addPersonOptions}
                  value={addPersonId}
                  onChange={setAddPersonId}
                  searchable
                  style={{ flex: 1 }}
                  size="md"
                />
                <Button
                  size="md"
                  leftSection={<IconPlus size={16} />}
                  onClick={handleAddParticipant}
                  loading={addingParticipant}
                  disabled={!addPersonId}
                >
                  Hinzufügen
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>
      )}

      {/* Section 3: Batch-add from tag-matched persons */}
      {isExisting && !isLocked && tagMatchedPersons.length > 0 && (
        <Paper shadow="sm" p="md" radius="md" withBorder data-tour="batch-add">
          <Title order={4} mb="xs">Personen mit passenden Tags ({tagMatchedPersons.length})</Title>
          <Text size="sm" c="dimmed" mb="sm">
            Diese Personen haben mindestens einen der Event-Tags und sind noch nicht Teilnehmer.
          </Text>
          <Stack gap="xs" mb="sm">
            {tagMatchedPersons.map((p) => (
              <Checkbox
                key={p.id}
                label={`${p.firstName} ${p.lastName}`}
                size="md"
                checked={batchSelected.includes(p.id.toString())}
                onChange={() => {
                  const sid = p.id.toString();
                  setBatchSelected((prev) =>
                    prev.includes(sid) ? prev.filter((v) => v !== sid) : [...prev, sid],
                  );
                }}
              />
            ))}
          </Stack>
          <Group gap="xs">
            <Button
              size="md"
              leftSection={<IconPlus size={16} />}
              onClick={handleBatchAdd}
              disabled={!batchSelected.length}
            >
              {batchSelected.length > 0 ? `${batchSelected.length} hinzufügen` : 'Auswählen...'}
            </Button>
            {batchSelected.length < tagMatchedPersons.length && (
              <Button
                size="md"
                variant="light"
                onClick={() => setBatchSelected(tagMatchedPersons.map((p) => p.id.toString()))}
              >
                Alle auswählen
              </Button>
            )}
            {batchSelected.length > 0 && (
              <Button size="md" variant="subtle" onClick={() => setBatchSelected([])}>
                Auswahl aufheben
              </Button>
            )}
          </Group>
        </Paper>
      )}

      {/* Section 4: Cancel form */}
      {isExisting && !event?.isCancelled && showCancelForm && (
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-orange-4)' }}>
          <Title order={4} mb="sm">Einheit stornieren</Title>
          <Textarea
            label="Grund (optional)"
            placeholder="z.B. schlechtes Wetter, Hallenproblem..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            autosize
            minRows={2}
            mb="sm"
          />
          <Group gap="xs">
            <Button color="orange" size="md" onClick={handleCancel}>Stornieren bestätigen</Button>
            <Button variant="subtle" size="md" onClick={() => setShowCancelForm(false)}>Abbrechen</Button>
          </Group>
        </Paper>
      )}

      {/* Sticky action buttons */}
      <Box
        data-tour="actions"
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--mantine-color-body)',
          padding: '12px 0',
          borderTop: '1px solid var(--mantine-color-default-border)',
          zIndex: 100,
        }}
      >
        <Group gap="sm" wrap="wrap">
          {(!isGraded || adminUnlocked) && (
            <Button onClick={handleSave} loading={saving} size="md">
              Speichern
            </Button>
          )}
          {!isGraded && isExisting && hasPresentParticipant && (
            <Button color="teal" onClick={handleGrade} loading={grading} size="md">
              Einheit bewerten
            </Button>
          )}
          {isExisting && !event?.isCancelled && !showCancelForm && (meId === Number(assignedPersonId) || canAdminEdit) && (
            <Button color="orange" variant="light" size="md" onClick={() => setShowCancelForm(true)}>
              Stornieren
            </Button>
          )}
          {(!isGraded || adminUnlocked) && isExisting && (
            <Button color="red" variant="light" onClick={handleDelete} size="md">
              Löschen
            </Button>
          )}
          <Button variant="subtle" onClick={() => navigate('/training/events')} size="md">
            Zurück
          </Button>
        </Group>
      </Box>
    </Stack>
  );
};

export default TrainingEventDetail;
